/*
 * Yappa Wishlist System
 * Custom Elements: <wishlist-button>, <wishlist-count>, <wishlist-items>
 * Storage: localStorage with key `{themeName}:wishlist`
 * Communication: theme.pubsub with 'wishlistUpdate' event
 */

(function () {
  'use strict';

  const STORAGE_KEY = `${theme.settings.themeName}:wishlist`;

  /* ─── WishlistManager (singleton) ─── */
  theme.wishlist = {
    getItems() {
      if (!theme.config.hasLocalStorage) return [];
      try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      } catch (e) {
        return [];
      }
    },

    setItems(items) {
      if (!theme.config.hasLocalStorage) return;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        // Safari private mode
      }
      this.publish();
    },

    addItem(productId) {
      productId = parseInt(productId);
      const items = this.getItems();
      if (!items.includes(productId)) {
        items.unshift(productId);
        this.setItems(items);
      }
    },

    removeItem(productId) {
      productId = parseInt(productId);
      const items = this.getItems().filter(id => id !== productId);
      this.setItems(items);
    },

    toggleItem(productId) {
      productId = parseInt(productId);
      if (this.hasItem(productId)) {
        this.removeItem(productId);
        return false;
      } else {
        this.addItem(productId);
        return true;
      }
    },

    hasItem(productId) {
      return this.getItems().includes(parseInt(productId));
    },

    getCount() {
      return this.getItems().length;
    },

    publish() {
      theme.pubsub.publish('wishlistUpdate', {
        items: this.getItems(),
        count: this.getCount()
      });
    },

    /* Merge customer metafield wishlist with localStorage on login */
    mergeCustomerData() {
      if (!theme.customer || !theme.customer.wishlist) return;
      const serverItems = Array.isArray(theme.customer.wishlist) ? theme.customer.wishlist : [];
      const localItems = this.getItems();
      const merged = [...new Set([...localItems, ...serverItems])];
      if (merged.length !== localItems.length) {
        this.setItems(merged);
      }
    }
  };

  // Merge on load if customer is logged in
  if (typeof theme.customer !== 'undefined') {
    theme.wishlist.mergeCustomerData();
  }

  /* ─── <wishlist-button> ─── */
  class WishlistButton extends HTMLButtonElement {
    constructor() {
      super();
    }

    wishlistUnsubscriber = undefined;

    connectedCallback() {
      this.productId = parseInt(this.getAttribute('data-product-id'));
      this.updateState();
      this.addEventListener('click', this.onClick.bind(this));
      this.wishlistUnsubscriber = theme.pubsub.subscribe('wishlistUpdate', this.updateState.bind(this));
    }

    disconnectedCallback() {
      if (this.wishlistUnsubscriber) {
        this.wishlistUnsubscriber();
      }
    }

    onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const added = theme.wishlist.toggleItem(this.productId);
      this.setAttribute('aria-pressed', added);
      this.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
    }

    updateState() {
      const inWishlist = theme.wishlist.hasItem(this.productId);
      this.setAttribute('aria-pressed', inWishlist);
      this.setAttribute('aria-label', inWishlist ? 'Remove from wishlist' : 'Add to wishlist');

      // Toggle icon visibility
      const heartEmpty = this.querySelector('.icon-heart');
      const heartFilled = this.querySelector('.icon-heart-filled');
      if (heartEmpty) heartEmpty.style.display = inWishlist ? 'none' : '';
      if (heartFilled) heartFilled.style.display = inWishlist ? '' : 'none';
    }
  }
  customElements.define('wishlist-button', WishlistButton, { extends: 'button' });

  /* ─── <wishlist-count> ─── */
  class WishlistCount extends HTMLElement {
    constructor() {
      super();
    }

    wishlistUnsubscriber = undefined;

    connectedCallback() {
      this.updateCount();
      this.wishlistUnsubscriber = theme.pubsub.subscribe('wishlistUpdate', this.updateCount.bind(this));
    }

    disconnectedCallback() {
      if (this.wishlistUnsubscriber) {
        this.wishlistUnsubscriber();
      }
    }

    updateCount() {
      const count = theme.wishlist.getCount();
      this.innerText = count;
      this.hidden = count === 0;
    }
  }
  customElements.define('wishlist-count', WishlistCount);

  /* ─── <wishlist-items> ─── */
  class WishlistItems extends HTMLElement {
    constructor() {
      super();
    }

    wishlistUnsubscriber = undefined;

    connectedCallback() {
      this.init();
      this.wishlistUnsubscriber = theme.pubsub.subscribe('wishlistUpdate', this.init.bind(this));
    }

    disconnectedCallback() {
      if (this.wishlistUnsubscriber) {
        this.wishlistUnsubscriber();
      }
    }

    init() {
      const queryString = this.getQueryString();
      if (!queryString) {
        this.innerHTML = `<div class="drawer__empty text-center p-8 text-opacity">${theme.wishlistStrings?.empty || 'Your wishlist is empty'}</div>`;
        this.dispatchEvent(new CustomEvent('is-empty'));
        return;
      }

      fetch(this.getAttribute('data-url') + queryString)
        .then(response => response.text())
        .then(responseText => {
          const sectionInnerHTML = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .querySelector('.shopify-section');

          const items = sectionInnerHTML.querySelector('recently-viewed') || sectionInnerHTML.querySelector('wishlist-items');
          if (items && items.innerHTML.trim().length) {
            this.innerHTML = items.innerHTML;
            this.dispatchEvent(new CustomEvent('wishlist:loaded'));
          } else {
            this.innerHTML = `<div class="drawer__empty text-center p-8 text-opacity">${theme.wishlistStrings?.empty || 'Your wishlist is empty'}</div>`;
            this.dispatchEvent(new CustomEvent('is-empty'));
          }
        })
        .catch(e => {
          console.error('Wishlist fetch error:', e);
        });
    }

    getQueryString() {
      const items = theme.wishlist.getItems();
      if (items.length === 0) return '';

      const limit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 10;
      return items
        .slice(0, limit)
        .map(id => `id:${id}`)
        .join(' OR ');
    }
  }
  customElements.define('wishlist-items', WishlistItems);

  /* ─── Header wishlist icon → open drawer to wishlist tab ─── */
  document.addEventListener('click', (e) => {
    const wishlistLink = e.target.closest('[data-wishlist-tab]');
    if (wishlistLink) {
      e.preventDefault();
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.setAttribute('data-open-tab', 'wishlist');
        drawer.show();
      }
    }
  });

  /* ─── ATC from wishlist tab → auto-switch back to cart tab ───
     When a form inside <wishlist-items> submits, mark a one-shot flag.
     On the next cart:refresh, click the first drawer tab (cart) so the
     user lands on their newly-added item instead of staying on the
     now-stale wishlist list. */
  let switchToCartOnRefresh = false;
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form && form.matches && form.matches('form') && form.closest('wishlist-items')) {
      switchToCartOnRefresh = true;
    }
  }, true);
  document.addEventListener('cart:refresh', () => {
    if (!switchToCartOnRefresh) return;
    switchToCartOnRefresh = false;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const firstTab = drawer.querySelector('.drawer__tab');
    if (firstTab && firstTab.getAttribute('aria-expanded') !== 'true') {
      firstTab.click();
    }
  });

  // Publish initial state so all counts update on page load
  theme.wishlist.publish();
})();
